import React from 'react';
import { Link } from 'react-router-dom';

const ProductHero = ({ product, category }) => {
  if (!product) {
    return null;
  }

  // Helper function to safely render text that might be a rich text object
  const renderText = (content) => {
    if (!content) return null;
    if (typeof content === 'string') return content;
    
    if (Array.isArray(content)) {
      return content.map(block => {
        if (typeof block === 'string') return block;
        if (block.children) {
          return block.children.map(child => 
            typeof child === 'string' ? child : child.text || ''
          ).join('');
        }
        return block.text || '';
      }).join(' ');
    }
    
    if (content.children && Array.isArray(content.children)) {
      return content.children.map(child => 
        typeof child === 'string' ? child : child.text || ''
      ).join('');
    }
    
    if (content.text) return content.text;
    return JSON.stringify(content);
  };

  // Get background image URL from product data
  const getBackgroundImage = () => {
    if (!product.BackgroundImage) return null;
    
    // Handle Strapi v4/v5 image structure
    const imageData = product.BackgroundImage.data || product.BackgroundImage;
    
    if (Array.isArray(imageData) && imageData.length > 0) {
      // Multiple images - use the first one
      const image = imageData[0];
      return image.attributes?.url || image.url;
    } else if (imageData && typeof imageData === 'object') {
      // Single image
      return imageData.attributes?.url || imageData.url;
    }
    
    return null;
  };

  const backgroundImageUrl = getBackgroundImage();
  const strapiUrl = process.env.REACT_APP_STRAPI_URL || 'http://localhost:1337';
  
  // Build full image URL if we have a background image
  const fullBackgroundUrl = backgroundImageUrl 
    ? (backgroundImageUrl.startsWith('http') ? backgroundImageUrl : `${strapiUrl}${backgroundImageUrl}`)
    : null;

  // Determine background style
  const backgroundStyle = fullBackgroundUrl
    ? {
        backgroundImage: `linear-gradient(rgba(30, 64, 175, 0.85), rgba(30, 64, 175, 0.55)), url(${fullBackgroundUrl})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }
    : {};

  const sectionClasses = fullBackgroundUrl
    ? "relative py-12"
    : "bg-gradient-to-br from-catalina-blue to-san-marino py-12";

  return (
    <section className={sectionClasses} style={backgroundStyle}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <nav className="mb-8">
          <ol className="flex items-center space-x-2 text-sm text-white/80">
            <li>
              <Link to="/" className="hover:text-white transition duration-300">
                Home
              </Link>
            </li>
            <li>
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
              </svg>
            </li>
            <li>
              <Link to="/products" className="hover:text-white transition duration-300">
                Products
              </Link>
            </li>
            {category && (
              <>
                <li>
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                </li>
                <li>
                  <Link 
                    to={`/products/${category.slug}`} 
                    className="hover:text-white transition duration-300"
                  >
                    {category.Name}
                  </Link>
                </li>
              </>
            )}
            <li>
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
              </svg>
            </li>
            <li className="text-white font-medium">
              {product.Title}
            </li>
          </ol>
        </nav>

        {/* Hero Content */}
        <div className="text-white">
          <div className="flex items-center gap-3 mb-4">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold">
              {product.Title}
            </h1>
            {product.featured && (
              <span className="bg-yellow-400 text-gray-900 px-3 py-1 rounded-full text-xs font-bold">
                Featured
              </span>
            )}
          </div>
          {/* {product.Description && (
            <p className="text-lg md:text-xl text-white/90 max-w-3xl leading-relaxed">
              {renderText(product.Description)}
            </p>
          )} */}
        </div>
      </div>
    </section>
  );
};

export default ProductHero;